/* eslint-disable no-shadow */

import {
    type GettersValues as BaseGettersValues,
    pickerTopBarModuleShared,
    type State as BaseState,
} from '@/app/store/seatAllocation/shared/pickerTopBar.ts';
import { guestOperationsSeatAllocationApi } from '@/app/shared/services/apis/guestOperationsSeatAllocationApi.service';
import { uniqueBy } from '@fifa-bsm/bo-frontend-shared/utils';
import { isNotEmpty } from '@fifa-bsm/bo-frontend-shared/validators';
import type { GetterTree } from 'vuex';

interface State extends BaseState {
    freezeMode: boolean;
    lastMinuteChanges: boolean;
    showPDFTemplateForSeatIndicator: boolean;
    showParticipantIdentifiers: boolean;
    mirrorSeatPlan: boolean;
    showAccessControlStatus: boolean;
    highlightNotDistributedSeats: boolean;
}

const state = (): State => ({
    ...pickerTopBarModuleShared.getInitialState(),
    freezeMode: false,
    lastMinuteChanges: false,
    showPDFTemplateForSeatIndicator: false,
    showParticipantIdentifiers: false,
    mirrorSeatPlan: false,
    showAccessControlStatus: false,
    highlightNotDistributedSeats: false,
});

export interface Areas {
    seatGroups: { value: number; label: string }[];
    blockLevels: { value: string; label: string }[];
}

export type AreaType = keyof Areas;

interface GettersValues extends BaseGettersValues {
    areas: Areas;
}

type Getters = GetterTree<State, State> &
    Record<
        keyof GettersValues,
        (
            state: State,
            getters: GettersValues,
            rootState: any,
            rootGetters: any
        ) => GettersValues[keyof GettersValues]
    >;

interface ActionContext {
    state: State;
    commit: (mutation: string, payload?: any) => void;
    dispatch: (action: string, payload?: any, config?: { root?: boolean }) => Promise<any>;
    getters: GettersValues;
    rootState: any;
    rootGetters: any;
}

const getters: Getters = {
    ...pickerTopBarModuleShared.getters,
    areas(state, getters, rootState, rootGetters): Areas {
        const user = rootGetters['User/user'];
        const userGroup = user.group.code;

        const blocks: { id: number; level: string | null }[] =
            rootGetters['SeatAllocation/guestOperations/Picker/computedSeatPlan']?.blocks ?? [];

        const blockLevels = uniqueBy(
            (block) => block.value,
            blocks
                .map((block) => ({ value: block.level, label: block.level }))
                .filter((block): block is { value: string; label: string } =>
                    isNotEmpty(block.value)
                )
        );

        const allSeats: any[] =
            rootGetters['SeatAllocation/guestOperations/Picker/allSeatPlanSeats'];
        const seatGroups = uniqueBy(
            (seatGroup) => seatGroup.value,
            allSeats
                .filter((seat) => seat.category?.seat_group?.type === userGroup)
                .map((seat) => ({
                    value: seat.category.seat_group.id,
                    label: seat.category.seat_group.name,
                }))
        );

        return { seatGroups, blockLevels };
    },
};

const actionsShared = {
    ...pickerTopBarModuleShared.actions({
        getEvents: (mainEventId) =>
            guestOperationsSeatAllocationApi
                .getEventsForMainEvent(mainEventId, {
                    page: 1,
                    perPage: 999,
                })
                .then((response) => response.data),
        getVenues: async (mainEventId) => {
            const response =
                await guestOperationsSeatAllocationApi.getEventListFilters(mainEventId);

            return response.venue_id.data;
        },
    }),
};

const actions = {
    ...actionsShared,
    selectEvent({ getters, commit, dispatch }: ActionContext, payload: any) {
        actionsShared.selectEvent({ getters, commit, dispatch } as ActionContext, payload);
        dispatch('SeatAllocation/guestOperations/LabelSettings/loadLabelSettings', payload, {
            root: true,
        });
    },
};

const mutations = {
    ...pickerTopBarModuleShared.mutations,
    initialize(storeState: State) {
        Object.assign(storeState, state());
    },
    setFreezeMode(state: State, enabled: boolean) {
        state.freezeMode = enabled;
    },
    setLastMinuteChanges(state: State, enabled: boolean) {
        state.lastMinuteChanges = enabled;
    },
    setShowPDFTemplateForSeatIndicator(state: State, enabled: boolean) {
        state.showPDFTemplateForSeatIndicator = enabled;
    },
    setShowParticipantIdentifiers(state: State, enabled: boolean) {
        state.showParticipantIdentifiers = enabled;
    },
    setMirrorSeatPlan(state: State, enabled: boolean) {
        state.mirrorSeatPlan = enabled;
    },
    setShowAccessControlStatus(state: State, enabled: boolean) {
        state.showAccessControlStatus = enabled;
    },
    setHighlightNotDistributedSeats(state: State, enabled: boolean) {
        state.highlightNotDistributedSeats = enabled;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
